import { FC, useState } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { useCreateCartMutation, useCreateMetafieldForOrderMutation, useUpdateOrderMutation } from '@services';
import { showToast } from '@store/slices/notification';
import { addErrorToLog } from '@store/slices/error-log';
import {
  Cart,
  CustomerAddressFormFields,
  JimboResponse,
  OrderMetafield,
  AvaSuburbHours,
  OrderMetafieldPermission,
  CustomerBlackListedData,
} from '@models';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { OrderStatusModal } from './OrderStatusModal';
import { toSydneyTime } from '@helpers';
import { RsbWarningModal } from './RsbWarningModal';

interface Props {
  isDeliveryFeeIncluded: boolean;
  avaTradingTimes: AvaSuburbHours | null;
  isBlacklisted?: boolean;
  isBlackListedData?: CustomerBlackListedData;
}

interface ProductList {
  quantity: number;
  name: string;
  unitPrice: number;
  productId: number;
  discountPercentage: number;
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(
  ({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: 'rgba(240, 122, 122)',
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 14,
      fontWeight: 400,
    },
  }),
);

const ConfirmOrder: FC<Props> = ({ isDeliveryFeeIncluded, avaTradingTimes, isBlacklisted, isBlackListedData }) => {
  const [orderStatusPopup, setOrderStatusPopup] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(0);

  const [rsbWarningModal, setRsbWarningModal] = useState(false);

  const [createMetafields] = useCreateMetafieldForOrderMutation();

  const customer = useAppSelector((state) => state.customer.value.selectedCustomer);
  const activeUser = useAppSelector((state) => state.activeUser.value);

  const {
    deliveryInstructions,
    products,
    recipient,
    shippingAddress,
    selectedPaymentMethod,
    totalPrice,
    orderDiscountPercentage,
    deliveryTime,
  } = useAppSelector((state) => state.order.value);

  const [createCart, { isLoading }] = useCreateCartMutation();
  const [updateOrder] = useUpdateOrderMutation();

  const dispatch = useAppDispatch();

  const minCartValue = selectedPaymentMethod?.id === 'noCharge' ? false : totalPrice < 20;

  const getTotalDiscount = () =>
    totalPrice * (orderDiscountPercentage || 0) +
    products.reduce(
      (acc: number, product: ProductList) => acc + product.quantity * product.unitPrice * (product.discountPercentage || 0),
      0,
    );

  const createCustomerOrder = () => {
    createCart({
      customerId: customer.id,
      body: {
        address: {
          first_name: customer.first_name,
          last_name: customer.last_name,
          email: customer.email,
          address1: customer.addresses[0].address1,
          address2: customer.addresses[0].address2,
          city: customer.addresses[0].city,
          state_or_province: customer.addresses[0].state_or_province,
          state_or_province_code: customer.addresses[0].state_or_province,
          country_code: customer.addresses[0].country_code,
          postal_code: customer.addresses[0].postal_code,
          phone: customer.phone,
          lat:
            customer.addresses[0].form_fields
              ?.find((field: { name: CustomerAddressFormFields }) => field.name === CustomerAddressFormFields.Lat)
              ?.value.toString() ?? '',
          lng:
            customer.addresses[0].form_fields
              ?.find((field: { name: CustomerAddressFormFields }) => field.name === CustomerAddressFormFields.Long)
              ?.value.toString() ?? '',
          gnafpid:
            customer.addresses[0].form_fields
              ?.find((field: { name: CustomerAddressFormFields }) => field.name === CustomerAddressFormFields.GNAFPID)
              ?.value.toString() ?? '',
          isRestrict:
            customer.addresses[0].form_fields
              ?.find((field: { name: CustomerAddressFormFields }) => field.name === CustomerAddressFormFields.ISRESTRICT)
              ?.value.toString() ?? '',
        },
        shipping_address: {
          first_name: recipient.first_name || customer.first_name,
          last_name: recipient.last_name || customer.last_name,
          email: customer.email,
          address1: shippingAddress.address1,
          address2: shippingAddress.address2,
          city: shippingAddress.city,
          state_or_province: shippingAddress.state_or_province,
          state_or_province_code: shippingAddress.state_or_province,
          country_code: shippingAddress.country_code,
          postal_code: shippingAddress.postal_code,
          phone: recipient.phone || customer.phone,
          lat:
            shippingAddress.form_fields
              ?.find((field: { name: CustomerAddressFormFields }) => field.name === CustomerAddressFormFields.Lat)
              ?.value.toString() ?? '',
          lng:
            shippingAddress.form_fields
              ?.find((field: { name: CustomerAddressFormFields }) => field.name === CustomerAddressFormFields.Long)
              ?.value.toString() ?? '',
          gnafpid:
            shippingAddress.form_fields
              ?.find((field: { name: CustomerAddressFormFields }) => field.name === CustomerAddressFormFields.GNAFPID)
              ?.value.toString() ?? '',
          isRestrict:
            shippingAddress.form_fields
              ?.find((field: { name: CustomerAddressFormFields }) => field.name === CustomerAddressFormFields.ISRESTRICT)
              ?.value.toString() ?? '',
        },
        items: {
          line_items: products.map((product: ProductList) => ({
            quantity: product.quantity,
            product_id: product.productId,
            list_price: product.unitPrice,
            name: product.name,
            discount_percentage: product.discountPercentage || 0,
          })),
        },
        isDeliveryFeeIncluded: selectedPaymentMethod?.id === 'noCharge' ? false : isDeliveryFeeIncluded,
        deliveryInstructions: deliveryInstructions,
        noChargePrice: selectedPaymentMethod?.id === 'noCharge' ? totalPrice : 0,
        discount_amount: getTotalDiscount(),
      },
    })
      .unwrap()
      .then(async (data: Cart) => {
        setCurrentOrderId(Number(data.order_id));
        setOrderStatusPopup(true);
        const metaFields: OrderMetafield[] = [
          {
            permission_set: OrderMetafieldPermission.write_and_sf_access,
            namespace: 'phone_order',
            key: 'jimbo_user',
            value: activeUser.user.email,
            description: 'Jimbo user email',
          },
          {
            permission_set: OrderMetafieldPermission.write_and_sf_access,
            namespace: 'phone_order',
            key: 'storedPaymentMethod',
            value: selectedPaymentMethod?.id === 'noCharge' ? 'no-charge' : selectedPaymentMethod.id,
            description: 'payment method id as returned from payment gateway',
          },
          {
            permission_set: OrderMetafieldPermission.write_and_sf_access,
            namespace: 'phone_order',
            key: 'paymentProvider',
            value: selectedPaymentMethod?.id === 'noCharge' ? 'no-charge' : 'gr4vy',
            description: 'payment provider from payment gateway',
          },
          {
            permission_set: OrderMetafieldPermission.write_and_sf_access,
            namespace: 'phone_order',
            key: 'orderChannel',
            value: 'Web',
            description: 'payment provider from payment gateway',
          },
          {
            permission_set: OrderMetafieldPermission.write_and_sf_access,
            namespace: 'phone_order',
            key: 'paymentMethod',
            value: selectedPaymentMethod?.id === 'noCharge' ? 'no-charge' : selectedPaymentMethod.method,
            description: 'payment provider from payment gateway',
          },
          {
            permission_set: OrderMetafieldPermission.write_and_sf_access,
            namespace: 'phone_order',
            key: 'envName',
            value: process.env.REACT_APP_PWA_ENV,
            description: 'payment provider from payment gateway',
          },
          {
            permission_set: OrderMetafieldPermission.write_and_sf_access,
            namespace: 'phone_order',
            key: 'requested_delivery_time',
            value: toSydneyTime(deliveryTime.requested_delivery_time, shippingAddress.state_or_province), //convert to sydney time
            description: 'Requested delivery time',
          },
          ...(deliveryTime.requested_delivery_end_time
            ? [
                {
                  permission_set: OrderMetafieldPermission.write_and_sf_access,
                  namespace: 'phone_order',
                  key: 'requested_delivery_end_time',
                  value: toSydneyTime(deliveryTime.requested_delivery_end_time, shippingAddress.state_or_province), //convert to sydney time
                  description: 'Requested delivery end time',
                },
              ]
            : []),
          {
            permission_set: OrderMetafieldPermission.write_and_sf_access,
            namespace: 'phone_order',
            key: 'invoiceData',
            value: data.invoiceData,
            description: 'Order invoice data',
          },
        ];

        if (selectedPaymentMethod?.id !== 'noCharge') {
          metaFields.push({
            permission_set: OrderMetafieldPermission.write_and_sf_access,
            namespace: 'phone_order',
            key: 'phoneOrder',
            value: 'true',
            description: 'true if order placed by phone',
          });
        }

        if (data) {
          await Promise.all(metaFields.map((metafield) => createMetafields({ orderId: Number(data.order_id), metafield })));
        }

        // Call update order after metafield update
        await updateOrder({
          id: data.order_id,
          payment_method:
            selectedPaymentMethod?.id === 'noCharge' ? 'no-charge' : `${selectedPaymentMethod?.scheme}-${selectedPaymentMethod?.method}`,
          status_id: selectedPaymentMethod?.id === 'noCharge' ? 11 : 7,
          customer_id: customer.id,
        });
      })
      .catch((error: { data: JimboResponse }) => {
        dispatch(addErrorToLog(error?.data));

        dispatch(
          showToast({
            message: `Failed to create the order.`,
            type: 'error',
          }),
        );
      });
  };

  const handleClick = async () => {
    if (!shippingAddress || !customer || !products.length) return;

    if (isBlacklisted) {
      setRsbWarningModal(true);
      return;
    }

    createCustomerOrder();
  };

  const handlePopupClose = () => {
    setOrderStatusPopup(false);
  };

  return (
    <>
      {avaTradingTimes ? (
        <Box display="flex" justifyContent="flex-end" padding={1} paddingRight={2}>
          <LightTooltip title="Cart value of an order must be at least $20.00" disableHoverListener={!minCartValue}>
            <LoadingButton
              disabled={!(products.length && shippingAddress && customer) || !(selectedPaymentMethod || minCartValue)}
              loading={isLoading}
              onClick={handleClick}
              variant="contained"
              data-testid="complete-order-button"
              sx={{
                '&.Mui-disabled': {
                  pointerEvents: 'auto',
                  backgroundColor: 'rgba(0, 0, 0, 0.26)',
                },
              }}
              component={minCartValue ? 'div' : 'button'}
            >
              Complete Order
            </LoadingButton>
          </LightTooltip>
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" padding={1} paddingRight={2}>
          <Typography variant="h6">Jimmy brings is not open in this suburb. Please select another to continue.</Typography>
        </Box>
      )}
      <RsbWarningModal
        isOpen={rsbWarningModal}
        onClose={() => {
          setRsbWarningModal(false);
        }}
        createOrder={() => {
          createCustomerOrder();
          setRsbWarningModal(false);
        }}
        isBlackListedData={isBlackListedData}
      />
      {orderStatusPopup && <OrderStatusModal isOpen={orderStatusPopup} onClose={handlePopupClose} orderId={currentOrderId} />}
    </>
  );
};

export default ConfirmOrder;
